import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { SystemService } from '@scripts/woo_services.module/SystemService';
import { TranslationService } from '@scripts/woo_services.module/TranslationService';
import { ModalContent } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { saveFileAs } from '../../../utils/files';
import { trackById } from '../../../utils/object';
import { RevenueReport, RevenueReportsService } from '../../../woo_services.module/RevenueReportsService';

@Component({
  selector: 'revenue-reports-page',
  templateUrl: './revenue-reports-page.component.html',
  styles: ['.override-bootstrap th {padding: 8px !important; font-weight: 500;}'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RevenueReportsPage implements OnInit {
  readonly systemService = inject(SystemService);
  availableReports = signal<RevenueReport[]>([]);

  erpInvoicesEnabled = signal(false);

  clientNameForReport(report: RevenueReport): string {
    if (report?.has_erp_files) {
      return 'Erp';
    } else if (report?.has_telia_files) {
      return 'Telia';
    } else {
      return 'Ingen intäktsfil';
    }
  }

  readonly trackById = trackById;

  translationService = inject(TranslationService);
  constructor(private modalService: ModalService, private revenueReportsService: RevenueReportsService) {}

  ngOnInit(): void {
    this.systemService.isFeatureToggleOn('erp_invoice_enabled').then((enabled) => {
      this.erpInvoicesEnabled.set(enabled);
    });

    this.revenueReportsService.getAll().then((reports) => {
      this.availableReports.set(reports);
    });
  }

  isSendDisabled(report: RevenueReport): boolean {
    // Never allow sending if there is no associated ERP or Telia file
    if (!report.has_erp_files && !report.has_telia_files) {
      return true;
    }

    // When ERP invoicing is enabled, Telia-only reports should not be sendable
    if (this.erpInvoicesEnabled() && report.has_telia_files && !report.has_erp_files) {
      return true;
    }

    // Otherwise, disable based on whether it has already been sent to the active system
    if (this.erpInvoicesEnabled()) {
      return !!report.sent_to_erp_at;
    } else {
      return !!report.sent_to_telia_at;
    }
  }

  hasRevenueFile(report: RevenueReport): boolean {
    return report.has_erp_files || report.has_telia_files;
  }

  downloadXlsx(report: RevenueReport): void {
    if (report.has_erp_files) {
      this.revenueReportsService.downloadErpXlsx(report.id).then((file) => {
        saveFileAs(file, `Intäktsfil ${report.month}-${report.year}.xlsx`);
      });
    } else if (report.has_telia_files) {
      this.revenueReportsService.downloadTeliaXlsx(report.id).then((file) => {
        saveFileAs(file, `Intäktsfil Telia ${report.month}-${report.year}.xlsx`);
      });
    }
  }

  downloadXml(report: RevenueReport): void {
    if (report.has_erp_files) {
      this.revenueReportsService.downloadErpXml(report.id).then((file) => {
        saveFileAs(file, `Intäktsfil ${report.month}-${report.year}.xml`);
      });
    } else if (report.has_telia_files) {
      this.revenueReportsService.downloadTeliaXml(report.id).then((file) => {
        saveFileAs(file, `Intäktsfil Telia ${report.month}-${report.year}.xml`);
      });
    }
  }

  sendToService(report: RevenueReport): void {
    this.modalService.openConfirm(this.sendConfirmDialogContent(this.clientNameForReport(report))).then((response) => {
      if (!response.confirm) return;

      const loaderModal = this.modalService.openLoader('Skickar...');
      const sendtoService = this.erpInvoicesEnabled()
        ? (id: any) => this.revenueReportsService.sendToErp(id)
        : (id: any) => this.revenueReportsService.sendToTelia(id);
      sendtoService(report.id).then(
        () => {
          this.revenueReportsService.get(report.id).then((newReport) => {
            const reports = this.availableReports();
            const index = reports.findIndex((r) => r.id === report.id);
            if (index !== -1) {
              const updatedReports = [...reports];
              updatedReports[index] = newReport;
              this.availableReports.set(updatedReports);
            }
          });
          loaderModal.close();
          this.modalService.openSuccess(`Intäktsfilen är nu skickad till ${this.clientNameForReport(report)}.`);
        },
        (err) => {
          loaderModal.close();
          this.modalService.openError(
            'Intäktsfilen kunde tyvärr inte skickas just nu, försök igen lite senare.',
            this.translationService.convertError(err?.error?.error),
          );
        },
      );
    });
  }

  private sendConfirmDialogContent(targetSystem: string): ModalContent {
    return {
      title: `Skicka intäktsfil till ${targetSystem}`,
      texts: [
        'En intäktsfil kan bara skickas en gång, du kan ladda ner en Excel-version av filen för att kontrollera innehållet i filen innan du skickar den.',
      ],
      confirmLabel: 'Skicka nu',
      cancelLabel: 'Avbryt',
    };
  }
}
